import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import type { StoryItem } from '../../types/api'
import { stripMarkdown } from '../../utils/stripMarkdown'
import ImageCaption from '../ImageCaption/ImageCaption'
import styles from './LeaderStory.module.css'

interface LeaderStoryProps {
  story: StoryItem
  size?: 'large' | 'medium' | 'small'
  /** When true: image renders on the left (horizontal layout); placeholder suppressed if no image */
  imageLeft?: boolean
  /** When true: excerpt is unclamped on wide screens to fill the square card */
  noImageSolo?: boolean
}

function renderMarkdownInline(md: string): string {
  const raw = marked.parseInline(md) as string
  return DOMPurify.sanitize(raw)
}

const sizeClass = (size: 'large' | 'medium' | 'small') =>
  size === 'large' ? styles.sizeLarge : size === 'medium' ? styles.sizeMedium : styles.sizeSmall

export default function LeaderStory({ story, size = 'large', imageLeft = false, noImageSolo = false }: LeaderStoryProps) {
  const { t } = useTranslation()
  const { lang = 'en', city = '' } = useParams<{ lang: string; city: string }>()
  const navigate = useNavigate()

  const titleHtml = useMemo(
    () => renderMarkdownInline(story.ai_title || ''),
    [story.ai_title],
  )

  const formattedDate = story.story_date
    ? new Date(story.story_date).toLocaleDateString(lang, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  const hasImage = Boolean(story.image_url)

  return (
    <article
      className={`${styles.card} ${sizeClass(size)}${hasImage && imageLeft ? ` ${styles.imageLeft}` : ''}${noImageSolo ? ` ${styles.noImageSolo}` : ''}`}
      onClick={() => navigate(`/${lang}/${city}/story/${story.cluster_id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/${lang}/${city}/story/${story.cluster_id}`) }}
      aria-label={story.ai_title}
    >
      {hasImage
        ? size === 'large' && !imageLeft
          ? (
            <figure className={styles.imageFigure}>
              <img src={story.image_url!} alt="" className={styles.storyImage} />
              <ImageCaption />
            </figure>
          )
          : <img src={story.image_url!} alt="" className={styles.storyImage} />
        : !imageLeft
          ? <div className={styles.imagePlaceholder} role="img" aria-label="Story image placeholder" />
          : null
      }

      <div className={styles.body}>
        <div className={styles.meta}>
          <span className={styles.categoryBadge}>{story.category}</span>
          <span className={styles.rankBadge} title={t('significance')}>
            ★ {story.rank.toFixed(1)}
          </span>
        </div>

        <h2
          className={styles.title}
          /* Title markdown is already sanitized by DOMPurify */
          dangerouslySetInnerHTML={{ __html: titleHtml }}
        />

        {story.ai_text_excerpt && (
          <p className={styles.excerpt}>{stripMarkdown(story.ai_text_excerpt)}</p>
        )}

        <div className={styles.footer}>
          {formattedDate || '—'}
        </div>
      </div>
    </article>
  )
}
